// Servicio de Asistencia/Marcaciones de personal
// Endpoints esperados del backend:
//   POST /attendance/check-in        -> crea una marcación de entrada
//   POST /attendance/check-out       -> cierra una marcación existente
//   POST /attendance/filter          -> lista marcaciones con paginación (limit, offset, query)
//   GET  /attendance/today           -> lista marcaciones de hoy
//   GET  /attendance/active          -> lista marcaciones abiertas (en turno)
//   POST /attendance/upload-photo    -> sube la foto (multipart/form-data) y devuelve la URL
//
// Si tu backend usa otra convención, ajusta las URLs aquí.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Sube una foto (Blob) y devuelve la URL donde quedó almacenada.
 * El backend debe responder { url: 'https://...' } o { data: { url: '...' } }.
 */
export const uploadAttendancePhoto = async (blob) => {
  const formData = new FormData();
  // El nombre del campo debe coincidir con lo que espera el backend (común: "photo" o "file")
  formData.append('photo', blob, `attendance-${Date.now()}.jpg`);

  const response = await fetch(`${API_URL}/attendance/upload-photo`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Error al subir la foto de evidencia');
  }

  const data = await response.json();
  return data?.data?.url || data?.url;
};

/**
 * Crea una marcación de entrada para un empleado.
 * @param {Object} params
 * @param {string} params.employeeId - ID del empleado (employee._id)
 * @param {string} [params.photoUrl] - URL de la foto de evidencia (opcional si el backend la sube)
 * @param {string} [params.photoBase64] - Alternativa: foto en base64 (si el backend la espera así)
 * @param {string} [params.observaciones] - Observaciones opcionales
 */
export const checkIn = async ({ employeeId, photoUrl, photoBase64, observaciones }) => {
  const body = { employeeId, observaciones };
  if (photoUrl) body.photoUrl = photoUrl;
  if (photoBase64) body.photoBase64 = photoBase64;

  const response = await fetch(`${API_URL}/attendance/check-in`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Error al registrar la entrada');
  }

  const data = await response.json();
  return data?.data || data;
};

/**
 * Cierra la marcación abierta de un empleado (registra salida + calcula horas).
 * @param {Object} params
 * @param {string} params.employeeId - ID del empleado
 * @param {string} [params.attendanceId] - ID de la marcación a cerrar (opcional si backend busca la abierta)
 * @param {string} [params.photoUrl]
 * @param {string} [params.photoBase64]
 * @param {string} [params.observaciones]
 */
export const checkOut = async ({ employeeId, attendanceId, photoUrl, photoBase64, observaciones }) => {
  const body = { employeeId, attendanceId, observaciones };
  if (photoUrl) body.photoUrl = photoUrl;
  if (photoBase64) body.photoBase64 = photoBase64;

  const response = await fetch(`${API_URL}/attendance/check-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Error al registrar la salida');
  }

  const data = await response.json();
  return data?.data || data;
};

/**
 * Lista marcaciones con paginación.
 * @param {Object} params
 * @param {number} params.limit
 * @param {number} params.offset
 * @param {Object} [params.query] - Filtros: { fechaInicio, fechaFin, employeeId, estado }
 */
export const getAttendances = async ({ limit = 20, offset = 1, query = {} } = {}) => {
  const response = await fetch(`${API_URL}/attendance/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit, offset, query }),
  });

  if (!response.ok) {
    throw new Error('Error al obtener las marcaciones');
  }

  const data = await response.json();
  return {
    data: data?.data || data?.records || [],
    totalRecords: data?.totalRecords || data?.total || 0,
  };
};

/**
 * Adaptador para ExportExcelButton.
 * Convierte { query, limit, page } -> { limit, offset, query } y normaliza
 * los filtros con rango de fechas ($gte/$lte) a strings YYYY-MM-DD
 * que es lo que espera el backend (campo `fecha` es string).
 */
export const exportAttendances = async ({ query = {}, limit = 10000, page = 1 } = {}) => {
  const normalized = { ...query };
  if (normalized.fecha && typeof normalized.fecha === 'object') {
    const r = normalized.fecha;
    if (r.$gte) normalized.fechaInicio = toFechaStringLocal(r.$gte);
    if (r.$lte) normalized.fechaFin = toFechaStringLocal(r.$lte);
    delete normalized.fecha;
  }
  const res = await getAttendances({ limit, offset: page, query: normalized });
  return res;
};

// Helper: Date -> 'YYYY-MM-DD' en zona local
const toFechaStringLocal = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Devuelve las marcaciones del día actual.
 * El backend debe filtrar por fecha del servidor.
 */
export const getTodayAttendances = async () => {
  const response = await fetch(`${API_URL}/attendance/today`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Error al obtener las marcaciones de hoy');
  }

  const data = await response.json();
  return data?.data || data || [];
};

/**
 * Devuelve las marcaciones que aún están sin cerrar (en turno).
 * Sirve para el módulo de salida: muestra quién está actualmente trabajando.
 */
export const getActiveAttendances = async () => {
  const response = await fetch(`${API_URL}/attendance/active`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Error al obtener las marcaciones activas');
  }

  const data = await response.json();
  return data?.data || data || [];
};
